import Input from '@/shared/components/rebranded/Input/Input';
import { useTranslations } from '@storebrand-digital/fms-translations-react';
import cx from 'classnames';
import format from 'date-fns/format';
import isAfter from 'date-fns/isAfter';
import isBefore from 'date-fns/isBefore';
import nb from 'date-fns/locale/nb';
import parseISO from 'date-fns/parseISO';
import DatePicker, { registerLocale } from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { PopoverContainer } from '../../pages/claimForms/components/PopoverContainer';
import { FieldSet, Label, Legend } from '../../shared/components/Form/Form';
import { FieldProps, ObjectSchema, StringSchema } from '../types';
import { joinErrors } from '../validation/utils';
import { hasErrors } from '../validation/validateSchema';
import { DATE_FORMATS, convertToDate } from './DateField/DateField';
import styles from './DateField/DateField.module.css';
import './react-datepicker.overrides.css';

registerLocale('nb', nb);

type DateRangeFieldProps = FieldProps<
  ObjectSchema<{ startDate: StringSchema; endDate: StringSchema }>
>;

export const DateRangeField = (props: DateRangeFieldProps) => {
  const {
    id,
    schema,
    name,
    value,
    onChange,
    touched,
    forceShowError,
    errors,
    readOnly
  } = props;

  const { getText } = useTranslations();

  const handleStartDateChange = (date: Date | null) => {
    let { startDate, endDate } = { startDate: '', endDate: '' };

    // reset endDate if you choose a startDate that is after endDate
    if (
      date &&
      value &&
      value.endDate &&
      isAfter(date, parseISO(value.endDate))
    ) {
      startDate = format(date, 'yyyy-MM-dd');
      endDate = '';
      return onChange({ target: { name, value: { startDate, endDate } } });
    }

    return onChange({
      target: {
        name,
        value: {
          startDate: date == null ? '' : format(date, 'yyyy-MM-dd'),
          endDate: value.endDate
        }
      }
    });
  };

  const handleEndDateChange = (date: Date | null) => {
    let { startDate, endDate } = { startDate: '', endDate: '' };

    // reset startDate if you choose an endDate that is before startDate
    if (
      date &&
      value &&
      value.startDate &&
      isBefore(date, parseISO(value.startDate))
    ) {
      startDate = '';
      endDate = format(date, 'yyyy-MM-dd');
      return onChange({ target: { name, value: { startDate, endDate } } });
    }

    return onChange({
      target: {
        name,
        value: {
          startDate: value.startDate,
          endDate: date == null ? '' : format(date, 'yyyy-MM-dd')
        }
      }
    });
  };

  const showError = !!(touched || forceShowError);

  const showStartDateError = showError && hasErrors(errors.startDate);

  const startDateError = showStartDateError
    ? joinErrors(errors.startDate, getText)
    : undefined;

  const showEndDateError = showError && hasErrors(errors.endDate);

  const endDateError = showEndDateError
    ? joinErrors(errors.endDate, getText)
    : undefined;

  const { startDate, endDate } = schema.properties!;

  const endDateId = `${id}_endDate`;
  const startDateId = `${id}_startDate`;

  return (
    <FieldSet>
      <div style={{ display: 'flex', alignItems: 'center' }}>
        {schema.title && <Legend>{schema.title}</Legend>}
      </div>
      {schema.$uiOptions?.hint && (
        <div style={{ marginBottom: 12, marginTop: 0 }}>
          <span className="dsm-form-hint" style={{ paddingTop: 0 }}>
            {schema.$uiOptions.hint}
          </span>
        </div>
      )}
      <div style={{ marginTop: 16 }}>
        <div style={{ display: 'flex' }}>
          <Label htmlFor={startDateId} error={showStartDateError}>
            {startDate.title}
          </Label>
          {startDate.description && (
            <PopoverContainer
              id={startDateId}
              description={startDate.description}
            />
          )}
        </div>
        <DatePicker
          id={`${id}_startDate`}
          locale="nb"
          dateFormat={DATE_FORMATS}
          autoComplete="off"
          placeholderText="DD.MM.YYYY"
          className={cx(styles.input, showStartDateError && styles.hasError)}
          calendarClassName={styles.calendar}
          maxDate={
            startDate.format === 'date-time-not-future' ||
            startDate.format === 'date-not-future'
              ? new Date()
              : undefined
          }
          selected={convertToDate(value.startDate)}
          startDate={convertToDate(value.startDate)}
          endDate={convertToDate(value.endDate)}
          onChange={handleStartDateChange}
          customInput={
            <Input
              id={`${id}_startDate_input`}
              name={`${id}_startDate_input`}
              inputProps={{
                className: styles.innerInput
              }}
              medium
              error={startDateError}
              onChange={() => {}}
              value=""
            />
          }
          selectsStart
          disabled={readOnly}
        />
        <div style={{ display: 'flex', marginTop: 16 }}>
          <Label htmlFor={`${id}_endDate`} error={showEndDateError}>
            {endDate.title}
          </Label>
          {endDate.description && (
            <PopoverContainer
              id={endDateId}
              description={endDate.description}
            />
          )}
        </div>
        <DatePicker
          id={`${id}_endDate`}
          locale="nb"
          autoComplete="off"
          dateFormat={DATE_FORMATS}
          placeholderText="DD.MM.YYYY"
          className={cx(styles.input, showEndDateError && styles.hasError)}
          calendarClassName={styles.calendar}
          maxDate={
            endDate.format === 'date-time-not-future' ||
            endDate.format === 'date-not-future'
              ? new Date()
              : undefined
          }
          selected={convertToDate(value.endDate)}
          startDate={convertToDate(value.startDate)}
          endDate={convertToDate(value.endDate)}
          onChange={handleEndDateChange}
          customInput={
            <Input
              id={`${id}_endDate_input`}
              name={`${id}_endDate_input`}
              inputProps={{
                className: styles.innerInput
              }}
              medium
              error={endDateError}
              onChange={() => {}}
              value=""
            />
          }
          selectsEnd
          disabled={readOnly}
        />
      </div>
    </FieldSet>
  );
};
